import { UserOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import React from 'react';

import type { ExperienceCategory } from 'app/typings/experiences';
import type { ExtranetExperience } from 'app/typings/packages';
import { assertNever } from 'app/utils/typing';

import type { GuestTask } from '../TaskCenter/Legacy/Types';
import { TaskName } from '../TaskCenter/Legacy/Types';

export const PER_PERSON = '/pers.';

export type PackageToggleSwitchParam = {
  packageId: number;
  isSwitchOn: boolean;
};

export const generatePackageSetIsSwitchOn =
  (
    packageId: number,
    toggleSwitches: PackageToggleSwitchParam[],
    setToggleSwitches: (toggleSwitches: PackageToggleSwitchParam[]) => void
  ) =>
  (isSwitchOn: boolean) => {
    const newToggleSwitches = [
      ...toggleSwitches.filter(
        (toogleSwitchParam) => toogleSwitchParam.packageId !== packageId
      ),
      { packageId, isSwitchOn },
    ];

    setToggleSwitches(newToggleSwitches);
  };

export const isSwitchOn = (
  packageId: number,
  toggleSwitches: PackageToggleSwitchParam[]
) =>
  !!toggleSwitches.find(
    (toogleSwitchParam) => toogleSwitchParam.packageId === packageId
  )?.isSwitchOn;

export const getSumOfPerPersonExperience = (
  experiences?: ExtranetExperience[]
) => {
  if (experiences) {
    return experiences.reduce((acc, exp) => {
      if (
        exp.price &&
        exp.priceDescription &&
        exp.priceDescription.endsWith(PER_PERSON)
      ) {
        return acc + exp.price;
      }

      return acc;
    }, 0);
  }

  return 0;
};

export const formatExperienceTooltipText = (
  experience: ExtranetExperience,
  experienceCategories: ExperienceCategory[],
  currencyFormatter: (price?: number | undefined) => string
) => {
  const experienceName =
    experienceCategories.find((cat) => cat.id === experience.categoryId)
      ?.name ?? experience.name;

  const price = currencyFormatter(experience.price ?? 0);
  const suffix = experience.priceDescription;

  return `${experienceName} (${price} ${suffix ?? ''})`;
};

export type ExtraGuestDetail = {
  priceConfiguration: {
    title: string;
    section: {
      title: string;
      icon: React.ReactElement;
      price: {
        firstPart: string;
        secondPart: string;
      };
      information: string;
    };
  };
  title: string;
  description: string;
  hasOverview: boolean;
  valueName: GuestTaskValueName;
};

export type GuestTaskValueName =
  | 'singleCustomerDiscount'
  | 'additionalAdultPrice'
  | 'additionalChildPrice';

export const getExtraGuestDetail = (taskName: GuestTask): ExtraGuestDetail => {
  switch (taskName) {
    case TaskName.AttractExtraGuests:
      return {
        priceConfiguration: {
          title: 'Extra guest fee configuration',
          section: {
            title: 'Allow extra adult fee',
            icon: <UsergroupAddOutlined />,
            price: {
              firstPart: 'Apply an additional fee of ',
              secondPart: 'per extra adult guests',
            },
            information:
              'This fee will be applied to the default total price (package price + room price)',
          },
        },
        title: 'Define extra guest fee',
        description:
          'Attract more than 2 adult guests by setting up your extra adult fee',
        hasOverview: true,
        valueName: 'additionalAdultPrice',
      };

    case TaskName.AttractSingleGuests:
      return {
        priceConfiguration: {
          title: 'Discount configuration',
          section: {
            title: 'Allow single guest discount',
            icon: <UserOutlined />,
            price: {
              firstPart: 'Apply a discount of ',
              secondPart: 'for single guests',
            },
            information:
              'This discount will be added to the total price (package price + room price)',
          },
        },
        title: 'Define single guest discount',
        description:
          'Attract single adult guests by setting up your single guest discount',
        hasOverview: false,
        valueName: 'singleCustomerDiscount',
      };

    case TaskName.ConfigureChildPrice:
      return {
        priceConfiguration: {
          title: 'Extra child fee configuration',
          section: {
            title: 'Configure extra child fee',
            icon: <UsergroupAddOutlined />,
            price: {
              firstPart: 'Apply an additional fee of ',
              secondPart: 'per extra child guests (2 - 12 years old)',
            },
            information:
              'This fee will be applied to the default total price (package price + room price)',
          },
        },
        title: 'Configure extra child fee',
        description:
          'Configure your package price for child guests (2 - 12 years old)',
        hasOverview: false,
        valueName: 'additionalChildPrice',
      };

    default:
      assertNever(taskName);
  }
};
